// Visual-redesign proposal page.  Pure styling preview — zero logic,
// zero API calls.  Shows the proposed palette, typography, cards,
// navigation and sample Schedule row at desktop + mobile widths.
// Route: /styleguide
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, Target, Repeat, History, Zap, Trophy, TrendingUp, Flame,
  Clock, ChevronRight, Menu, Search, BarChart3,
} from "lucide-react";

const PALETTE = [
  { name: "Ink",        hex: "#0b0f14", role: "App background" },
  { name: "Panel",      hex: "#121821", role: "Card surface" },
  { name: "Panel-2",    hex: "#1a2230", role: "Elevated card" },
  { name: "Border",     hex: "#232d3f", role: "Hairline divider" },
  { name: "Text",       hex: "#e6edf3", role: "Primary text" },
  { name: "Text muted", hex: "#8b96a7", role: "Secondary text" },
  { name: "Accent",     hex: "#f5b84d", role: "Primary action (amber)" },
  { name: "Positive",   hex: "#4ade80", role: "Win / live" },
  { name: "Danger",     hex: "#ef5a66", role: "Stop / loss" },
  { name: "Info",       hex: "#6ea8ff", role: "Information" },
];

const nav = [
  { label: "Schedule",  icon: Calendar, active: true },
  { label: "Live Now",  icon: Zap,      active: false },
  { label: "Autopilot", icon: Target,   active: false },
  { label: "History",   icon: History,  active: false },
  { label: "Accuracy",  icon: Trophy,   active: false },
];

const SAMPLE_PICKS = [
  { home: "Sassuolo", away: "Lazio", league: "Serie A",
    country: "Italy", market: "HT BTTS", pick: "No", conf: 74.8, ko: "18:00" },
  { home: "AS Roma",  away: "Cagliari", league: "Serie A",
    country: "Italy", market: "HT BTTS", pick: "No", conf: 74.1, ko: "20:45" },
  { home: "Nantes",   away: "Rennes", league: "Ligue 1",
    country: "France", market: "Double Chance", pick: "Home or Draw",
    conf: 81.3, ko: "20:00" },
];

export default function StyleguidePage() {
  const [tab, setTab] = useState("preview");
  return (
    <div
      className="min-h-screen w-full"
      style={{
        background:
          "radial-gradient(1200px 600px at 10% -20%, rgba(245,184,77,0.08), transparent 60%), " +
          "radial-gradient(900px 500px at 110% 10%, rgba(110,168,255,0.05), transparent 60%), " +
          "#0b0f14",
        fontFamily:
          '"Space Grotesk", "Inter", ui-sans-serif, system-ui, sans-serif',
        color: "#e6edf3",
      }}
    >
      {/* Fonts loaded locally to avoid dev-time CDN wait */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap"
        rel="stylesheet"
      />

      {/* ─────── Top navigation (desktop) ─────── */}
      <nav className="hidden md:flex items-center justify-between px-8 py-4 border-b"
           style={{ borderColor: "#232d3f", backdropFilter: "blur(10px)" }}>
        <div className="flex items-center gap-10">
          <Logo />
          <div className="flex items-center gap-1">
            {nav.map(({ label, icon: Icon, active }) => (
              <button key={label}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition"
                style={{
                  background: active ? "rgba(245,184,77,0.12)" : "transparent",
                  color: active ? "#f5b84d" : "#8b96a7",
                  border: active ? "1px solid rgba(245,184,77,0.25)" : "1px solid transparent",
                }}>
                <Icon className="h-4 w-4" />
                <span className="font-medium">{label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="h-9 w-9 rounded-full flex items-center justify-center"
                  style={{ background: "#121821", border: "1px solid #232d3f" }}>
            <Search className="h-4 w-4" style={{ color: "#8b96a7" }} />
          </button>
          <div className="px-4 py-1.5 rounded-full text-xs flex items-center gap-2"
               style={{ background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.25)",
                        color: "#4ade80", fontFamily: '"JetBrains Mono",monospace' }}>
            <span className="h-1.5 w-1.5 rounded-full inline-block"
                  style={{ background: "#4ade80", boxShadow: "0 0 8px #4ade80" }} />
            BAL&nbsp;$1,243.80
          </div>
          <div className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold"
               style={{ background: "linear-gradient(135deg,#f5b84d,#e68a2e)", color: "#0b0f14" }}>
            NK
          </div>
        </div>
      </nav>

      {/* ─────── Top bar (mobile) ─────── */}
      <nav className="md:hidden flex items-center justify-between px-4 py-3 border-b"
           style={{ borderColor: "#232d3f" }}>
        <button className="p-2 -ml-2">
          <Menu className="h-5 w-5" style={{ color: "#e6edf3" }} />
        </button>
        <Logo compact />
        <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold"
             style={{ background: "linear-gradient(135deg,#f5b84d,#e68a2e)", color: "#0b0f14" }}>
          NK
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12 pb-32 md:pb-12 space-y-16">
        {/* ─────── v1 ↔ v2 toggle ─────── */}
        <div className="flex justify-center -mt-4" data-testid="styleguide-toggle">
          <div
            className="inline-flex items-center rounded-full p-1 text-xs"
            style={{ background: "#121821", border: "1px solid #232d3f" }}
          >
            <span
              className="px-4 py-1.5 rounded-full font-medium"
              style={{
                background: "rgba(245,184,77,0.14)",
                color: "#f5b84d",
                border: "1px solid rgba(245,184,77,0.3)",
                fontFamily: '"JetBrains Mono",monospace',
              }}
              data-testid="styleguide-toggle-v1-current"
            >
              v1 · Amber
            </span>
            <Link
              to="/styleguide-v2"
              className="px-4 py-1.5 rounded-full transition hover:text-slate-200"
              style={{ color: "#8b96a7" }}
              data-testid="styleguide-toggle-v2"
            >
              v2 · Cyan
            </Link>
            <Link
              to="/styleguide-v3"
              className="px-4 py-1.5 rounded-full transition hover:text-slate-200"
              style={{ color: "#8b96a7" }}
              data-testid="styleguide-toggle-v3"
            >
              v3 · Quiet
            </Link>
          </div>
        </div>

        {/* ─────── Hero ─────── */}
        <section>
          <div className="text-xs uppercase tracking-[0.2em]"
               style={{ color: "#f5b84d", letterSpacing: "0.2em", fontWeight: 500 }}>
            Design Proposal · v1
          </div>
          <h1 className="mt-3 text-4xl md:text-6xl font-semibold leading-[1.05]"
              style={{ letterSpacing: "-0.02em" }}>
            Clean, calm,<br />
            <span style={{
              background: "linear-gradient(90deg,#f5b84d,#e68a2e 60%,#c76a1a)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>institutionally sharp.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base md:text-lg" style={{ color: "#8b96a7" }}>
            Deep ink base with a single warm accent. Space Grotesk for display,
            Inter for body, JetBrains Mono for numbers. Generous spacing,
            hairline borders, and a mobile bottom-nav that stays out of the way.
          </p>
        </section>

        {/* ─────── Palette ─────── */}
        <section>
          <SectionTitle number="01" title="Palette" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
            {PALETTE.map(c => (
              <div key={c.name} className="rounded-xl p-4"
                   style={{ background: "#121821", border: "1px solid #232d3f" }}>
                <div className="h-16 w-full rounded-lg mb-3"
                     style={{ background: c.hex, border: "1px solid rgba(255,255,255,0.04)" }} />
                <div className="text-sm font-medium">{c.name}</div>
                <div className="text-xs" style={{ color: "#8b96a7",
                     fontFamily: '"JetBrains Mono",monospace' }}>{c.hex}</div>
                <div className="text-xs mt-1" style={{ color: "#8b96a7" }}>{c.role}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ─────── Typography ─────── */}
        <section>
          <SectionTitle number="02" title="Typography" />
          <div className="rounded-xl p-6 md:p-10 mt-6 space-y-6"
               style={{ background: "#121821", border: "1px solid #232d3f" }}>
            <div>
              <div className="text-xs" style={{ color: "#8b96a7" }}>Display · Space Grotesk 56/−2%</div>
              <div className="text-5xl md:text-6xl font-semibold"
                   style={{ letterSpacing: "-0.02em" }}>Today's edge.</div>
            </div>
            <div>
              <div className="text-xs" style={{ color: "#8b96a7" }}>Heading · Space Grotesk 24/−1%</div>
              <div className="text-2xl font-semibold">Top 20 Elite picks</div>
            </div>
            <div>
              <div className="text-xs" style={{ color: "#8b96a7" }}>Body · Inter 16</div>
              <div className="text-base max-w-xl"
                   style={{ fontFamily: '"Inter",sans-serif' }}>
                Clean, calm reading. One accent colour. The data does the work.
              </div>
            </div>
            <div>
              <div className="text-xs" style={{ color: "#8b96a7" }}>Numbers · JetBrains Mono 20</div>
              <div className="text-xl font-medium" style={{ fontFamily: '"JetBrains Mono",monospace' }}>
                1.4293 · 74.8% · $1,243.80
              </div>
            </div>
          </div>
        </section>

        {/* ─────── Sample card (Schedule row) ─────── */}
        <section>
          <SectionTitle number="03" title="Sample card — Schedule pick" />
          <div className="mt-6 space-y-3">
            {SAMPLE_PICKS.map((p, i) => (
              <PickCard key={i} {...p} />
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
            <KPITile label="Queued" value="18" tone="muted" />
            <KPITile label="Placed" value="12" tone="positive" />
            <KPITile label="Realised" value="+$84.20" tone="accent" />
            <KPITile label="Hit rate" value="74%" tone="info" />
          </div>
        </section>

        {/* ─────── Navigation samples ─────── */}
        <section>
          <SectionTitle number="04" title="Navigation" />
          <div className="rounded-xl p-6 mt-6"
               style={{ background: "#121821", border: "1px solid #232d3f" }}>
            <div className="text-xs mb-3" style={{ color: "#8b96a7" }}>Pill tab bar (used on Autopilot/Top-20):</div>
            <div className="flex items-center gap-1 overflow-x-auto -mx-6 px-6">
              {["Overview", "Daily Autopilot", "X5 Rollover", "T20 Rollover", "History"].map((t, i) => (
                <button key={t}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition"
                  style={{
                    background: i === 0 ? "rgba(245,184,77,0.12)" : "transparent",
                    color: i === 0 ? "#f5b84d" : "#8b96a7",
                    border: i === 0 ? "1px solid rgba(245,184,77,0.25)" : "1px solid #232d3f",
                  }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl mt-6 overflow-hidden"
               style={{ background: "#121821", border: "1px solid #232d3f" }}>
            <div className="text-xs p-4 pb-0" style={{ color: "#8b96a7" }}>Mobile bottom-nav:</div>
            <div className="p-6">
              <div className="h-16 rounded-2xl flex items-center justify-around px-2"
                   style={{ background: "#0b0f14", border: "1px solid #232d3f" }}>
                {nav.map(({ label, icon: Icon, active }) => (
                  <button key={label}
                    className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl"
                    style={{ background: active ? "rgba(245,184,77,0.12)" : "transparent" }}>
                    <Icon className="h-5 w-5"
                      style={{ color: active ? "#f5b84d" : "#8b96a7" }} />
                    <span className="text-[10px]"
                      style={{ color: active ? "#f5b84d" : "#8b96a7" }}>{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="h-24" />
      </main>

      {/* Mobile bottom nav (live) */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 p-3"
           style={{ background: "linear-gradient(180deg, transparent, #0b0f14 50%)" }}>
        <div className="h-16 rounded-2xl flex items-center justify-around px-2"
             style={{ background: "#121821", border: "1px solid #232d3f",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.4)" }}>
          {nav.map(({ label, icon: Icon, active }) => (
            <button key={label}
              className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl"
              style={{ background: active ? "rgba(245,184,77,0.12)" : "transparent" }}>
              <Icon className="h-5 w-5"
                style={{ color: active ? "#f5b84d" : "#8b96a7" }} />
              <span className="text-[10px]"
                style={{ color: active ? "#f5b84d" : "#8b96a7" }}>{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const Logo = ({ compact = false }) => (
  <div className="flex items-center gap-2">
    <div className="h-8 w-8 rounded-lg flex items-center justify-center"
         style={{ background: "linear-gradient(135deg,#f5b84d,#c76a1a)",
                  boxShadow: "0 4px 20px rgba(245,184,77,0.25)" }}>
      <Flame className="h-4 w-4" style={{ color: "#0b0f14" }} strokeWidth={2.5} />
    </div>
    {!compact && (
      <span className="text-base font-semibold tracking-tight"
            style={{ letterSpacing: "-0.01em" }}>
        NDAKU<span style={{ color: "#f5b84d" }}>BET</span>
      </span>
    )}
  </div>
);

const SectionTitle = ({ number, title }) => (
  <div className="flex items-baseline gap-4">
    <span className="text-xs tracking-[0.2em]" style={{
      color: "#8b96a7", fontFamily: '"JetBrains Mono",monospace',
    }}>{number}</span>
    <h2 className="text-2xl md:text-3xl font-semibold"
        style={{ letterSpacing: "-0.015em" }}>{title}</h2>
  </div>
);

const PickCard = ({ home, away, league, country, market, pick, conf, ko }) => (
  <div className="rounded-xl p-5 flex items-center justify-between gap-4 transition"
       style={{ background: "#121821", border: "1px solid #232d3f" }}>
    <div className="flex items-start gap-4 flex-1 min-w-0">
      <div className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0"
           style={{ background: "rgba(245,184,77,0.08)",
                    border: "1px solid rgba(245,184,77,0.2)" }}>
        <span className="text-base font-semibold" style={{ color: "#f5b84d" }}>
          {Math.round(conf)}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-xs mb-1"
             style={{ color: "#8b96a7" }}>
          <Clock className="h-3 w-3" /> {ko}
          <span>·</span>
          <span className="truncate">{league} · {country}</span>
        </div>
        <div className="font-semibold text-base md:text-lg leading-snug">
          {home} <span style={{ color: "#8b96a7" }}>vs</span> {away}
        </div>
        <div className="mt-1.5 text-sm" style={{ color: "#8b96a7" }}>
          <span className="font-medium" style={{ color: "#e6edf3" }}>{market}</span>
          <span className="mx-1.5">→</span>
          <span style={{ color: "#f5b84d" }}>{pick}</span>
        </div>
      </div>
    </div>
    <div className="hidden md:flex flex-col items-end gap-1">
      <div style={{ color: "#8b96a7" }} className="text-xs">confidence</div>
      <div className="text-xl font-semibold"
           style={{ color: "#4ade80", fontFamily: '"JetBrains Mono",monospace' }}>
        {conf.toFixed(1)}%
      </div>
    </div>
    <ChevronRight className="h-4 w-4" style={{ color: "#8b96a7" }} />
  </div>
);

const KPITile = ({ label, value, tone }) => {
  const colour = {
    muted:    "#e6edf3",
    positive: "#4ade80",
    accent:   "#f5b84d",
    info:     "#6ea8ff",
  }[tone] || "#e6edf3";
  return (
    <div className="rounded-xl p-4"
         style={{ background: "#121821", border: "1px solid #232d3f" }}>
      <div className="text-xs uppercase tracking-wider" style={{ color: "#8b96a7" }}>{label}</div>
      <div className="text-2xl md:text-3xl font-semibold mt-1.5"
           style={{ color: colour, fontFamily: '"JetBrains Mono",monospace',
                    letterSpacing: "-0.02em" }}>
        {value}
      </div>
    </div>
  );
};
